import type { Locale } from './index';
import { useLocale } from './index';

// Map Aura Nota locales to Intl locale strings
const INTL_LOCALE: Record<Locale, string> = {
  'pt-BR': 'pt-BR',
  'en': 'en-US',
  'es': 'es-419',
  'zh-CN': 'zh-CN',
  'fr': 'fr-FR',
  'it': 'it-IT',
};

// Date formatting
export function formatDate(date: string | null | undefined, locale: Locale): string {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(date));
  } catch {
    return String(date);
  }
}

// DateTime formatting (with time)
export function formatDateTime(date: string | null | undefined, locale: Locale): string {
  if (!date) return '—';
  try {
    return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  } catch {
    return String(date);
  }
}

// Currency formatting (BRL for now, but formatted per locale)
export function formatCurrency(value: string | number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) : value;
  if (isNaN(num)) return String(value);
  try {
    return new Intl.NumberFormat(INTL_LOCALE[locale], {
      style: 'currency',
      currency: 'BRL',
    }).format(num);
  } catch {
    return String(value);
  }
}

// Number formatting
export function formatNumber(value: string | number | null | undefined, locale: Locale, decimals = 2): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  try {
    return new Intl.NumberFormat(INTL_LOCALE[locale], {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  } catch {
    return String(value);
  }
}

// Confidence formatting (percentage)
export function formatConfidence(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined) return '—';
  try {
    return new Intl.NumberFormat(INTL_LOCALE[locale], {
      style: 'percent',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value * 100)}%`;
  }
}

/**
 * Hook that returns all formatters bound to the current locale.
 * Use inside any component wrapped by <LocaleProvider>.
 */
export function useFormatters() {
  const { locale } = useLocale();
  return {
    formatDate: (date: string | null | undefined) => formatDate(date, locale),
    formatDateTime: (date: string | null | undefined) => formatDateTime(date, locale),
    formatCurrency: (value: string | number | null | undefined) => formatCurrency(value, locale),
    formatNumber: (value: string | number | null | undefined, decimals?: number) => formatNumber(value, locale, decimals),
    formatConfidence: (value: number | null | undefined) => formatConfidence(value, locale),
  };
}
